import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { organizationsService } from '../services/organizations.service';
import { Organization } from '../types';
import { toast } from 'react-toastify';

interface DeleteModalState {
  isOpen: boolean;
  orgId: string;
  orgName: string;
  blocking: Record<string, number> | null;
  autoRemovable: Record<string, number> | null;
  totalBlocking: number;
  totalAutoRemovable: number;
  canDelete: boolean;
  loading: boolean;
  confirmText: string;
}

const OrganizationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [deletedOrganizations, setDeletedOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [filterActive, setFilterActive] = useState<string>('all');
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    isOpen: false,
    orgId: '',
    orgName: '',
    blocking: null,
    autoRemovable: null,
    totalBlocking: 0,
    totalAutoRemovable: 0,
    canDelete: false,
    loading: false,
    confirmText: '',
  });

  const handleToggleSms = async (id: string, currentValue: boolean) => {
    try {
      await organizationsService.update(id, { smsEnabled: !currentValue });
      toast.success(
        !currentValue
          ? t('organizations.smsActivated')
          : t('organizations.smsDeactivated')
      );
      loadOrganizations();
    } catch (error: any) {
      console.error('Failed to toggle SMS:', error);
      toast.error(t('organizations.failedToToggleSms'));
    }
  };

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const filters: {
        isActive?: boolean;
      } = {};

      if (filterActive !== 'all')
        filters.isActive = filterActive === 'active';

      const data = await organizationsService.getAll(filters);
      setOrganizations(data);
    } catch (error: any) {
      console.error('Failed to load organizations:', error);
      toast.error(t('organizations.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadDeletedOrganizations = async () => {
    try {
      const data = await organizationsService.getDeleted();
      setDeletedOrganizations(data);
    } catch (error: any) {
      console.error('Failed to load deleted organizations:', error);
    }
  };

  const handleRestore = async (id: string, name: string) => {
    if (!window.confirm(t('organizations.restoreConfirm', { name }))) {
      return;
    }

    try {
      await organizationsService.restore(id);
      toast.success(t('organizations.restored', { name }));
      loadOrganizations();
      loadDeletedOrganizations();
    } catch (error: any) {
      console.error('Failed to restore organization:', error);
      const errorMessage =
        error.response?.data?.message ||
        t('organizations.failedToRestore');
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    loadOrganizations();
    loadDeletedOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterActive]);

  const handleDeactivate = async (id: string, name: string) => {
    if (!window.confirm(t('organizations.deactivateConfirm', { name }))) {
      return;
    }

    try {
      await organizationsService.deactivate(id);
      toast.success(t('organizations.deactivated'));
      loadOrganizations();
    } catch (error: any) {
      console.error('Failed to deactivate organization:', error);
      const errorMessage =
        error.response?.data?.message ||
        t('organizations.failedToDeactivate');
      toast.error(errorMessage);
    }
  };

  const handleOpenDeleteModal = async (id: string, name: string) => {
    setDeleteModal({
      isOpen: true,
      orgId: id,
      orgName: name,
      blocking: null,
      autoRemovable: null,
      totalBlocking: 0,
      totalAutoRemovable: 0,
      canDelete: false,
      loading: true,
      confirmText: '',
    });

    try {
      const depData = await organizationsService.getDependencies(id);
      setDeleteModal((prev) => ({
        ...prev,
        blocking: depData.blocking,
        autoRemovable: depData.autoRemovable,
        totalBlocking: depData.totalBlocking,
        totalAutoRemovable: depData.totalAutoRemovable,
        canDelete: depData.canDelete,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Failed to check dependencies:', error);
      toast.error(t('organizations.failedToCheckDependencies'));
      setDeleteModal((prev) => ({ ...prev, isOpen: false }));
    }
  };

  const handlePermanentDelete = async () => {
    if (deleteModal.confirmText !== deleteModal.orgName) return;

    try {
      await organizationsService.delete(deleteModal.orgId);
      toast.success(t('organizations.deleted'));
      setDeleteModal((prev) => ({ ...prev, isOpen: false }));
      loadOrganizations();
      loadDeletedOrganizations();
    } catch (error: any) {
      console.error('Failed to delete organization:', error);
      const errorMessage =
        error.response?.data?.message ||
        t('organizations.failedToDelete');
      toast.error(errorMessage);
    }
  };

  const dependencyLabels: Record<string, string> = {
    users: t('organizations.depUsers'),
    transfers: t('organizations.depTransfers'),
    drivers: t('organizations.depDrivers'),
    providers: t('organizations.depProviders'),
    customers: t('organizations.depCustomers'),
    pricing: t('organizations.depPricing'),
    vehicleTypes: t('organizations.depVehicleTypes'),
    customerProviderMappings: t('organizations.depMappings'),
    accountTransactions: t('organizations.depTransactions'),
    notifications: t('organizations.depNotifications'),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('organizations.title')}</h1>
          <button
            onClick={() => navigate('/app/organizations/new')}
            className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors min-h-[44px] font-medium"
          >
            + {t('organizations.addOrganization')}
          </button>
        </div>

        <div className="flex gap-4 mb-4 sm:mb-6">
          <div className="flex-1 sm:flex-none">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.status')}
            </label>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            >
              <option value="all">{t('common.all')}</option>
              <option value="active">{t('common.active')}</option>
              <option value="inactive">{t('common.inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      {organizations.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400 text-lg">{t('organizations.noOrganizations')}</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {organizations.map((org) => (
              <li key={org.id}>
                <div className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                          {org.name}
                        </h3>
                        {org.isActive ? (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            {t('common.active')}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                            {t('common.inactive')}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="truncate">
                          <span className="font-medium">{t('organizations.email')}:</span>{' '}
                          {org.contactEmail}
                        </div>
                        {org.contactPhone && (
                          <div className="truncate">
                            <span className="font-medium">{t('organizations.phone')}:</span>{' '}
                            {org.contactPhone}
                          </div>
                        )}
                        {org.parentOrganization && (
                          <div className="truncate">
                            <span className="font-medium">{t('organizations.parent')}:</span>{' '}
                            {org.parentOrganization.name}
                          </div>
                        )}
                        {org.childOrganizations &&
                          org.childOrganizations.length > 0 && (
                            <div>
                              <span className="font-medium">
                                {t('organizations.children')}:
                              </span>{' '}
                              {org.childOrganizations.length}
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:ml-4">
                      {/* SMS Toggle */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-500 dark:text-gray-400">SMS</span>
                        <button
                          onClick={() => handleToggleSms(org.id, org.smsEnabled || false)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            org.smsEnabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                          role="switch"
                          aria-checked={org.smsEnabled || false}
                          aria-label={`SMS ${org.smsEnabled ? 'on' : 'off'}`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              org.smsEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                      <button
                        onClick={() => navigate(`/app/organizations/${org.id}`)}
                        className="flex-1 sm:flex-none px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors min-h-[40px] font-medium"
                      >
                        {t('common.edit')}
                      </button>
                      {org.isActive ? (
                        <button
                          onClick={() => handleDeactivate(org.id, org.name)}
                          className="flex-1 sm:flex-none px-3 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 active:bg-amber-700 transition-colors min-h-[40px] font-medium"
                        >
                          {t('organizations.deactivate')}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleOpenDeleteModal(org.id, org.name)}
                          className="flex-1 sm:flex-none px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors min-h-[40px] font-medium"
                        >
                          {t('organizations.permanentDelete')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Deleted Organizations Section */}
      {deletedOrganizations.length > 0 && (
        <div className="mt-8">
          <button
            onClick={() => setShowDeleted(!showDeleted)}
            className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors mb-4"
          >
            <span className={`transform transition-transform ${showDeleted ? 'rotate-90' : ''}`}>&#9654;</span>
            {t('organizations.deletedOrganizations')} ({deletedOrganizations.length})
          </button>

          {showDeleted && (
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-red-200 dark:divide-red-800">
                {deletedOrganizations.map((org) => (
                  <li key={org.id}>
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="text-base font-medium text-gray-900 dark:text-white">
                              {org.name}
                            </h3>
                            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                              {t('organizations.deleted')}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {org.contactEmail}
                            {(org as any).deletedAt && (
                              <span className="ml-3">
                                {t('organizations.deletedAt')}: {new Date((org as any).deletedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRestore(org.id, org.name)}
                          className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors min-h-[40px] font-medium"
                        >
                          {t('organizations.restore')}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full p-6 shadow-xl">
            <h3 className="text-lg font-bold text-red-700 dark:text-red-400 mb-4">
              {t('organizations.permanentDeleteTitle')}
            </h3>

            {deleteModal.loading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('common.loading')}
              </div>
            ) : (
              <>
                {/* Warning */}
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-800 dark:text-red-300 font-medium">
                    {t('organizations.permanentDeleteWarning')}
                  </p>
                </div>

                {/* Blocking dependencies */}
                {deleteModal.totalBlocking > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">
                      {t('organizations.hasDependencies', { count: deleteModal.totalBlocking })}
                    </p>
                    <ul className="text-sm text-amber-700 dark:text-amber-400 space-y-1">
                      {deleteModal.blocking && Object.entries(deleteModal.blocking)
                        .filter(([, count]) => count > 0)
                        .map(([key, count]) => (
                          <li key={key} className="flex justify-between">
                            <span>{dependencyLabels[key] || key}</span>
                            <span className="font-semibold">{count}</span>
                          </li>
                        ))}
                    </ul>
                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-3">
                      {t('organizations.removeDependenciesFirst')}
                    </p>
                  </div>
                )}

                {/* Auto-removable info */}
                {deleteModal.totalAutoRemovable > 0 && deleteModal.canDelete && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">
                      {t('organizations.autoRemovableInfo')}
                    </p>
                    <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                      {deleteModal.autoRemovable && Object.entries(deleteModal.autoRemovable)
                        .filter(([, count]) => count > 0)
                        .map(([key, count]) => (
                          <li key={key} className="flex justify-between">
                            <span>{dependencyLabels[key] || key}</span>
                            <span className="font-semibold">{count}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

                {/* Confirm by typing name */}
                {deleteModal.canDelete && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {t('organizations.typeToConfirm', { name: deleteModal.orgName })}
                    </p>
                    <input
                      type="text"
                      value={deleteModal.confirmText}
                      onChange={(e) =>
                        setDeleteModal((prev) => ({
                          ...prev,
                          confirmText: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                      placeholder={deleteModal.orgName}
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() =>
                      setDeleteModal((prev) => ({ ...prev, isOpen: false }))
                    }
                    className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors font-medium"
                  >
                    {t('common.cancel')}
                  </button>
                  {deleteModal.canDelete && (
                    <button
                      onClick={handlePermanentDelete}
                      disabled={deleteModal.confirmText !== deleteModal.orgName}
                      className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {t('organizations.confirmPermanentDelete')}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationsPage;
